// Angemeldeter Benutzer und Rechte. Die Rechte prüft der Server; hier steuern sie nur, was sichtbar ist.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from "react";
import { ABGEMELDET, api, daten, type Schemas } from "../api/client";

export type Ich = Schemas["Ich"];

type Sitzung = {
  ich: Ich | null;
  laedt: boolean;
  darf: (recht: string) => boolean;
  darfEines: (rechte: readonly string[]) => boolean;
  anmelden: (kuerzel: string, passwort: string) => Promise<Ich>;
  /** `hinweis` erscheint auf der Anmeldeseite (z. B. „Passwort geändert – bitte mit dem neuen Passwort anmelden.“) */
  abmelden: (hinweis?: string) => Promise<void>;
};

const HINWEIS = "imho:anmeldehinweis";

/** Hinweis für die Anmeldeseite merken – gilt auch über das Programm-Fenster hinaus (gleiche Herkunft, ein Tab). */
function hinweisMerken(text: string, ueberschreiben = true): void {
  try {
    if (ueberschreiben || !sessionStorage.getItem(HINWEIS)) sessionStorage.setItem(HINWEIS, text);
  } catch { /* ohne Speicher eben ohne Hinweis */ }
}

/** Gemerkter Hinweis für die Anmeldeseite; `loeschen` nach dem Anzeigen (einmal zeigen). */
// eslint-disable-next-line react-refresh/only-export-components
export function anmeldeHinweis(loeschen = false): string | null {
  try {
    const t = sessionStorage.getItem(HINWEIS);
    if (loeschen) sessionStorage.removeItem(HINWEIS);
    return t;
  } catch {
    return null;
  }
}

const Kontext = createContext<Sitzung | null>(null);

async function ichLaden(): Promise<Ich | null> {
  const antwort = await api.GET("/api/auth/ich");
  if (antwort.response.status === 401) return null;
  return daten(antwort);
}

export function SitzungProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const abfrage = useQuery({ queryKey: ["ich"], queryFn: ichLaden, staleTime: 60_000, retry: false });

  useEffect(() => {
    const weg = () => {
      if (qc.getQueryData(["ich"])) hinweisMerken("Die Sitzung ist beendet – bitte neu anmelden.", false);
      qc.setQueryData(["ich"], null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "ich" });
    };
    window.addEventListener(ABGEMELDET, weg);
    return () => window.removeEventListener(ABGEMELDET, weg);
  }, [qc]);

  const ich = abfrage.data ?? null;
  const rechte = useMemo(() => new Set(ich?.rechte ?? []), [ich]);
  const darf = useCallback((recht: string) => rechte.has(recht), [rechte]);
  const darfEines = useCallback((liste: readonly string[]) => liste.length === 0 || liste.some((r) => rechte.has(r)), [rechte]);

  const anmelden = useCallback(async (kuerzel: string, passwort: string) => {
    const neu = await daten(api.POST("/api/auth/anmelden", { body: { kuerzel, passwort } }));
    qc.setQueryData(["ich"], neu);
    return neu;
  }, [qc]);

  const abmelden = useCallback(async (hinweis?: string) => {
    if (hinweis) hinweisMerken(hinweis);
    try {
      await api.POST("/api/auth/abmelden");
    } finally {
      // erst „ich“ leeren, dann den Rest: qc.clear() entfernt die beobachtete Abfrage – der Provider bekäme das null
      // dann nie mit und /anmelden schickte mit dem alten Benutzer gleich wieder zurück (Schleife 31.10.)
      qc.setQueryData(["ich"], null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "ich" });
    }
  }, [qc]);

  const wert = useMemo<Sitzung>(() => ({ ich, laedt: abfrage.isPending, darf, darfEines, anmelden, abmelden }),
    [ich, abfrage.isPending, darf, darfEines, anmelden, abmelden]);
  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSitzung(): Sitzung {
  const s = useContext(Kontext);
  if (!s) throw new Error("useSitzung außerhalb von SitzungProvider");
  return s;
}
